import * as React from "react";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { ITab } from "../../types/ITab";
import TabComponent from "./TabComponent";
import { displayRepresentationFlag, removeTab } from "../../../redux/reducers/RepertoireReducer";
import CarouselScrollButton from "../carouselScrollButton/CarouselScrollButton";
import { ITabReduxItem } from "../../../redux/types/ITabReduxItem";
import IRepertoireComponentDataItem from "../../../redux/types/IRepertoireComponentDataItem";
import { IYesNoPromptViewModalProps } from "../modalViews/yesNoPromptView/YesNoPromptView";
import { hideModal, showYesNoPrompt } from "../../../redux/reducers/ModalReducer";
import { WORK_INTRAY_TAB_ICON, WORK_INTRAY_TAB_ICON_TRANSFORM, INTRAY, WORK_REPERTOIRE_TAB_ICON, WORK_REPERTOIRE_TAB_ICON_TRANSFORM, PRODUCT_INTRAY_TAB_ICON, PRODUCT_INTRAY_TAB_ICON_TRANSFORM, PRODUCT_REPERTOIRE_TAB_ICON, PRODUCT_REPERTOIRE_TAB_ICON_TRANSFORM, AGREEMENT_INTRAY_TAB_ICON, AGREEMENT_INTRAY_TAB_ICON_TRANSFORM, AGREEMENT_REPERTOIRE_TAB_ICON, AGREEMENT_REPERTOIRE_TAB_ICON_TRANSFORM, TABS_VIEW_MODAL_HEADER } from "../../Consts";
import { setCurrentPage } from "../../../redux/reducers/HomeReducer";
import { MATCHING_ENGINE, MEMBERS_PORTAL } from "../../../accounts/Consts";
import IShortCutKeys from "../../../redux/types/IShortCutKeys";
import { FIRST_PANE_INDEX, SECOND_PANE_INDEX } from "../../RepertoirePageContainer";
import { debounce } from "lodash";
import { ErrorBoundary } from "../../../telemetry/components/ErrorBoundary";

export interface ITabsViewProps {
    tabs: ITabReduxItem[];
    tabsPaneTwo: ITabReduxItem[];
    tabsViewData: IRepertoireComponentDataItem;
    removeTab: typeof removeTab;
    activeTab: number;
    activeTabPaneTwo: number;
    openTab: (index: number, pane: number) => void;
    setActivePane: (pane: number) => void;
    closeTabDisabled: boolean;
    hideModal?: typeof hideModal;
    showYesNoPrompt?: typeof showYesNoPrompt;
    displayRepresentation: typeof displayRepresentationFlag;
    setCurrentPage: typeof setCurrentPage;
    portalType: string;
    displayModal: boolean;
    shortCutKeys: IShortCutKeys[];
    activePane: number;
    closeAllTabsInSecondPane: () => void;
}

const META_DATA_KEY: Record<string, { link: string }> = {
    'find work': { link: 'works' },
    'find agreement': { link: 'agreements' },
    'find ip': { link: 'ips' },
    'find claim': { link: 'claims' },
    'find artist': { link: 'artists' },
    'find product': { link: 'products' },
    'find workflow': { link: 'workflows' },
    'find usage information': { link: 'usage' },
    'find usage pools': { link: 'pools' },
    'find distribution': { link: 'distributions' },
    'find payment run': { link: 'paymentRuns' },
    'find license': { link: 'licenses' },
    'find adjustment': { link: 'adjustments' },
};

const inferMetadataKey = (tabItem: ITabReduxItem): string | null => {
    if (!tabItem) return null;
    if (tabItem.workMaintenanceState) return 'find work';
    if (tabItem.agreementMaintenanceState) return 'find agreement';
    if (tabItem.interestedPartyMaintenanceState) return 'find ip';
    if (tabItem.artistMaintenanceState) return 'find artist';
    if (tabItem.productMaintenanceState) return 'find product';
    if (tabItem.workflowSearchResult || tabItem.workflowSearchQuery) return 'find workflow';
    if (tabItem.findUsageMaintenanceState || tabItem.usageMaintenanceState) return 'find usage information';
    if (tabItem.usagePoolMaintenanceState) return 'find usage pools';
    if (tabItem.distributionMaintenanceState) return 'find distribution';
    if (tabItem.claimMaintenanceState) return 'find claim';
    if (tabItem.paymentRunMaintenanceState) return 'find payment run';
    if (tabItem.licenseMaintenanceState) return 'find license';
    if (tabItem.adjustmentMaintenanceState) return 'find adjustment';
    return null;
};

const navigateToMetaPage = (tabItem: ITabReduxItem, portalType: string, setCurrentPage: (url: string) => void) => {
    const address = portalType === MEMBERS_PORTAL ? '/works/' : '/repertoire/';
    const inferredKey = inferMetadataKey(tabItem);
    const lowerTitle = tabItem?.title?.toLowerCase();
    const keyToUse = inferredKey || (lowerTitle && META_DATA_KEY[lowerTitle] ? lowerTitle : null);

    if (keyToUse && META_DATA_KEY[keyToUse]) {
        setCurrentPage(address + META_DATA_KEY[keyToUse].link);
    }
};


const TabsView: React.FC<ITabsViewProps> = (
    {
        tabs,
        tabsPaneTwo,
        tabsViewData,
        removeTab,
        activeTab,
        activeTabPaneTwo,
        openTab,
        setActivePane,
        closeTabDisabled,
        hideModal,
        showYesNoPrompt,
        displayRepresentation,
        setCurrentPage,
        portalType,
        displayModal,
        shortCutKeys,
        activePane,
        closeAllTabsInSecondPane,
    },
) => {

    const secondPaneRef = React.useRef<HTMLDivElement>(null);
    const firstPaneRef = React.useRef<HTMLDivElement>(null);

    const Rkeys = shortCutKeys.find(key => key.actionCode === 'SWITCH_TAB_RIGHT')?.key.split(',') ?? ['Control', 'ArrowRight'];
    const Lkeys = shortCutKeys.find(key => key.actionCode === 'SWITCH_TAB_LEFT')?.key.split(',') ?? ['Control', 'ArrowLeft'];
    React.useEffect(() => {
        const handleKeyDown = debounce((e: KeyboardEvent) => {
            if (e.ctrlKey && !displayModal && (e.key === Rkeys[1] || e.key === Lkeys[1])) {
                e.preventDefault();
                const isNext = e.key === "ArrowRight";
                const tabsList = activePane === FIRST_PANE_INDEX ? tabs : tabsPaneTwo;
                const activeTabIndex = activePane === FIRST_PANE_INDEX ? activeTab : activeTabPaneTwo;
                const newTabIndex = (activeTabIndex + (isNext ? 1 : -1) + tabsList.length) % tabsList.length;
                openTab(newTabIndex, activePane);
            }
        }, 200);
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [activeTab, activeTabPaneTwo, activePane, tabs, tabsPaneTwo, displayModal, openTab]);

    React.useEffect(() => {
        const onClickOutsideSecondPane = () => {
            setActivePane(0);
        };
        const onClickInsideSecondPane = () => {
            setActivePane(1)
        };

        const handleMakeAPaneActive = (event) => {
            if (displayModal) return;
            if (secondPaneRef.current && !secondPaneRef.current.contains(event.target)) {
                onClickOutsideSecondPane();
            }
            if (secondPaneRef.current && secondPaneRef.current.contains(event.target)) {
                onClickInsideSecondPane();
            }

            if (firstPaneRef.current && firstPaneRef.current.contains(event.target)) {
                onClickOutsideSecondPane();
            }
            let documentTitle = document.title;
            documentTitle = tabs[activeTab].title;
            document.title = documentTitle;
        }

        const close = (e) => {
            const escapeKey = shortCutKeys && shortCutKeys.find(key => key.actionCode === 'CLOSE_ACTIVE_TAB') ? shortCutKeys.find(key => key.actionCode === 'CLOSE_ACTIVE_TAB')?.key : 'Escape';
            if (e.key === escapeKey && !displayModal) {
                onClickRemoveTab(activeTab, activePane);
            }
        }
        window.addEventListener('keydown', close)
        window.addEventListener('click', handleMakeAPaneActive);
        return () => {
            window.removeEventListener('click', handleMakeAPaneActive);
            window.removeEventListener('keydown', close)
        }
    }, [activeTab, activeTabPaneTwo, activePane, tabs, tabsPaneTwo, displayModal, openTab])



    const onClickRemoveTab = (index: number, paneIndex: number) => {
        if (closeTabDisabled) return;

        const tabsList = paneIndex === FIRST_PANE_INDEX ? tabs : tabsPaneTwo;
        const activeIndex = paneIndex === FIRST_PANE_INDEX ? activeTab : activeTabPaneTwo;
        const isClosingActiveTab = paneIndex === activePane && index === activeIndex;

        let newActiveIndex = activeIndex;

        if (isClosingActiveTab) {
            if (tabsList.length === 1) {
                newActiveIndex = -1;
            } else if (index === tabsList.length - 1) {
                newActiveIndex = index - 1;
            } else {
                newActiveIndex = index;
            }
        } else if (index < activeIndex) {
            newActiveIndex = activeIndex - 1;
        }

        const updateAfterRemoval = () => {
            const newTabs = paneIndex === FIRST_PANE_INDEX
                ? tabs.filter((_, i) => i !== index)
                : tabsPaneTwo.filter((_, i) => i !== index);

            const nextTab = newTabs[newActiveIndex];

            removeTab(index, paneIndex);

            if (isClosingActiveTab) {
                if (nextTab && newActiveIndex >= 0) {
                    openTab(newActiveIndex, paneIndex);
                } else {
                    openTab(-1, paneIndex);
                }
                navigateToMetaPage(nextTab, portalType, setCurrentPage);
            }
        };
        const shouldPrompt = (
            (paneIndex === FIRST_PANE_INDEX && tabs[index]?.changesMade) ||
            (paneIndex === SECOND_PANE_INDEX && tabsPaneTwo[index]?.changesMade)
        );

        if (shouldPrompt) {
            const message: string = tabsViewData.fields[0]?.data ?? 'Confirm Close';
            const props: IYesNoPromptViewModalProps = {
                yesCallback: () => {
                    hideModal();
                    updateAfterRemoval();
                },
                noCallback: () => hideModal()
            };
            showYesNoPrompt(TABS_VIEW_MODAL_HEADER, props, undefined, message);
        } else {
            updateAfterRemoval();
        }
    };

    const onClick = async (index, paneIndex) => {
        await openTab(index, paneIndex);
        const tabItem = paneIndex === FIRST_PANE_INDEX ? tabs[index] : tabsPaneTwo[index];
        navigateToMetaPage(tabItem, portalType, setCurrentPage);
    };
    const removeTabOnClick = async (index: number, paneIndex: number) => {
        await onClickRemoveTab(index, paneIndex)
    };

    const renderTabs = (tabs, paneIndex, activeTab) => {
        return tabs.map((tab: ITab, index: number) => (
            <div data-testid={activeTab === index ? "activeTab" : "tab"} onClick={(e) => onClick(index, paneIndex)} key={index} className={activeTab === index ? "activeTab" : "tab"}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 100 100">
                    {tabs[index].workMaintenanceState && tabs[index].workMaintenanceState.work ?
                        (tabs[index].workMaintenanceState.work.dataSource === INTRAY ?
                            <path id="" className="tabIconType" d={WORK_INTRAY_TAB_ICON} transform={WORK_INTRAY_TAB_ICON_TRANSFORM}></path>
                            : <path id="" className="tabIconType" d={WORK_REPERTOIRE_TAB_ICON} transform={WORK_REPERTOIRE_TAB_ICON_TRANSFORM}></path>)
                        : tabs[index].productMaintenanceState && tabs[index].productMaintenanceState.product ?
                            (tabs[index].productMaintenanceState.product.dataSource === INTRAY ?
                                <path id="" className="tabIconType" d={PRODUCT_INTRAY_TAB_ICON} transform={PRODUCT_INTRAY_TAB_ICON_TRANSFORM}></path>
                                : <path id="" className="tabIconType" d={PRODUCT_REPERTOIRE_TAB_ICON} transform={PRODUCT_REPERTOIRE_TAB_ICON_TRANSFORM}></path>)
                            : tabs[index].agreementMaintenanceState && tabs[index].agreementMaintenanceState.agreement ?
                                (tabs[index].agreementMaintenanceState.agreement.dataSource === INTRAY ?
                                    <path id="" className="tabIconType" d={AGREEMENT_INTRAY_TAB_ICON} transform={AGREEMENT_INTRAY_TAB_ICON_TRANSFORM}></path>
                                    : <path id="" className="tabIconType" d={AGREEMENT_REPERTOIRE_TAB_ICON} transform={AGREEMENT_REPERTOIRE_TAB_ICON_TRANSFORM}></path>)
                                : <></>
                    }
                </svg>

                <div>
                    {tab.title ? tab.title : "untitled"}
                </div>
                <div
                    className={["removeTabDiv", !!closeTabDisabled && "closeDisabled"].join(" ")}
                >
                    <span
                        className={`${paneIndex}`}
                        aria-hidden="true"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClickRemoveTab(index, paneIndex);
                        }}
                    >
                        <Dismiss20Regular />
                    </span>

                </div>
            </div>
        ));
    };

    const renderTabContent = (tabsContent, paneIndex, currentActiveTab) => {
        return tabsContent.map((tab: ITab, index: number) => {
            const stableKey = `pane-${paneIndex}-tab-${index}`;
            const isActive = currentActiveTab === index;
            return (
                <div
                    key={stableKey}
                    role="tabpanel"
                    className={`tab-pane fade ${isActive ? 'in active' : ''} ${tab.isReadonly ? 'versionHistory' : ''}`}
                    style={{ display: isActive ? 'block' : 'none' }}
                >
                    <ErrorBoundary key={stableKey} pageName={tab.title}>
                        <TabComponent component={tab.component} displayRepresentation={displayRepresentation} paneIndex={paneIndex} />
                    </ErrorBoundary>
                </div>
            );
        });
    };

    const hasSplitScreen = !!tabsPaneTwo?.length;
    return (
        <div className="flexRow">
            <div ref={firstPaneRef} className={`${hasSplitScreen && 'TabsViewSplitScreen'} form-group col-md-12 TabsView`}>
                <div className={`${hasSplitScreen && 'hasSplitScreen'} tabsDiv fixedTabs`}>
                    <CarouselScrollButton
                        content={renderTabs(tabs, FIRST_PANE_INDEX, activeTab)}
                        distanceToScroll={100}
                    />
                </div>
                <div
                    id="tab-container"
                    className="tab-content formBox tab-pane active fade in scrollableContent"
                >
                    {renderTabContent(tabs, FIRST_PANE_INDEX, activeTab)}
                </div>

            </div>
            {tabsPaneTwo && tabsPaneTwo.length > 0 && (
                <div ref={secondPaneRef} className={`${hasSplitScreen && 'TabsViewSplitScreen'} form-group col-md-12 TabsView`}>
                    <div style={{overflow: 'hidden'}} className={`${hasSplitScreen && 'hasSplitScreen'} tabsDiv fixedTabs`}>
                        <div style={{width: '95%'}}>
                            <CarouselScrollButton
                                content={renderTabs(tabsPaneTwo, SECOND_PANE_INDEX, activeTabPaneTwo)}
                                distanceToScroll={100}
                            />
                        </div>
                        <button type="button" onClick={() => closeAllTabsInSecondPane()}
                              style={{ backgroundColor: 'white', border: 'none', marginBottom: 16 }}>
                             <Dismiss20Regular aria-hidden="true" />
                        </button>
                    </div>
                    <div
                        id="tab-container-pane-two"
                        className="tab-content formBox tab-pane active fade in scrollableContent"
                    >
                        {renderTabContent(tabsPaneTwo, SECOND_PANE_INDEX, activeTabPaneTwo)}
                    </div>
                </div>
            )}
        </div>
    );
}

export default TabsView;